import { useState, useRef, useCallback } from 'react';

const IS_DEV = import.meta.env.VITE_DEV_AUTH === '1';

/**
 * PhotoUpload — drag-drop + file-pick photo upload component.
 *
 * Props:
 *   photos      — array of { url, caption?, uploadedAt? }
 *   onAdd(photo) — called with the new photo object
 *   onRemove(url) — called with the url to remove
 *   entityId    — id of the parent entity
 *   entityType  — 'estimate' | 'work_order'
 *   maxPhotos   — max number of photos (default 10)
 */
export default function PhotoUpload({
  photos = [],
  onAdd,
  onRemove,
  entityId: _entityId,
  entityType: _entityType = 'estimate',
  maxPhotos = 10,
}) {
  const [dragging, setDragging] = useState(false);
  const [loadingIds, setLoadingIds] = useState(new Set());
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const atMax = photos.length >= maxPhotos;

  async function processFile(file) {
    if (!file || !file.type.startsWith('image/')) return;
    if (photos.length >= maxPhotos) return;

    const tempId = `upload-${Date.now()}-${Math.random()}`;
    setLoadingIds(prev => new Set(prev).add(tempId));

    try {
      if (IS_DEV) {
        // Dev mode: create object URL for preview, no actual upload
        const url = URL.createObjectURL(file);
        const photo = { url, caption: file.name, uploadedAt: new Date().toISOString() };
        onAdd?.(photo);
      } else {
        // TODO: upload to Supabase Storage at
        // `photos/{orgId}/${entityType}/${entityId}/${filename}`
        // const { data, error } = await supabase.storage
        //   .from('photos')
        //   .upload(`${orgId}/${entityType}/${entityId}/${file.name}`, file);
        // if (error) throw error;
        // const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(data.path);
        // onAdd?.({ url: publicUrl, caption: file.name, uploadedAt: new Date().toISOString() });
        const url = URL.createObjectURL(file);
        const photo = { url, caption: file.name, uploadedAt: new Date().toISOString() };
        onAdd?.(photo);
      }
    } finally {
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(tempId);
        return next;
      });
    }
  }

  async function handleFiles(files) {
    for (const file of Array.from(files)) {
      await processFile(file);
    }
  }

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragging(false);
    await handleFiles(e.dataTransfer.files);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length, maxPhotos]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragging(false);
  }, []);

  function handleFileInputChange(e) {
    handleFiles(e.target.files);
    e.target.value = '';
  }

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      {!atMax && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`relative rounded-xl border-2 border-dashed transition-colors cursor-pointer select-none ${
            dragging
              ? 'border-[#2E8BF0] bg-blue-50 dark:bg-blue-900/10'
              : 'border-gray-200 dark:border-[#3D5068] hover:border-[#2E8BF0] hover:bg-blue-50/50 dark:hover:bg-blue-900/5'
          } p-6 text-center`}
        >
          <div className="flex flex-col items-center gap-2 pointer-events-none">
            <svg className="w-8 h-8 text-[#64748B] dark:text-[#7D93AE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <div>
              <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE]">
                Drop photos here or click to browse
              </p>
              <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">
                {photos.length}/{maxPhotos} photos
              </p>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Camera capture (mobile) */}
      {!atMax && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-[#3D5068] text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors"
          >
            <svg className="w-4 h-4 text-[#64748B] dark:text-[#7D93AE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
            </svg>
            Take Photo
          </button>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="camera"
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>
      )}

      {/* Loading spinners for in-progress uploads */}
      {loadingIds.size > 0 && (
        <div className="flex items-center gap-2 text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
          <svg className="w-4 h-4 animate-spin text-[#2E8BF0]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Uploading {loadingIds.size} photo{loadingIds.size !== 1 ? 's' : ''}…
        </div>
      )}

      {/* Thumbnail grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, idx) => (
            <div key={photo.url || idx} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-[#243348]">
              <img
                src={photo.url}
                alt={photo.caption || `Photo ${idx + 1}`}
                className="w-full h-full object-cover"
              />
              {/* Remove button */}
              <button
                type="button"
                onClick={() => onRemove?.(photo.url)}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                title="Remove photo"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
